import { Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import {
  COMMON_FIELDS,
  CONDITION_OPERATORS,
  GUARD_TYPES,
  type Condition,
} from './workflowModel';

interface Props {
  condition: Condition;
  onChange: (condition: Condition) => void;
  readOnly?: boolean;
  depth?: number;
}

const INPUT =
  'px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-[11px] focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60';

type GroupKey = 'all' | 'any';

function isGroup(c: Condition): c is { all: Condition[] } | { any: Condition[] } {
  return Boolean(c && typeof c === 'object' && ('all' in c || 'any' in c));
}
function isNot(c: Condition): c is { not: Condition } {
  return Boolean(c && typeof c === 'object' && 'not' in c);
}
function isGuard(c: Condition): c is { guard: string } {
  return Boolean(c && typeof c === 'object' && 'guard' in c);
}
function isField(c: Condition): c is { field: string; op: string; value?: unknown } {
  return Boolean(c && typeof c === 'object' && 'field' in c);
}

const blankLeaf = (): Condition => ({ field: 'priority', op: 'eq', value: '' });

/**
 * Builds a condition without writing JSON.
 *
 * The grammar is data rather than an expression language precisely so it can be
 * rendered and rebuilt like this — and so a workflow author can never express
 * something that executes. Mirrors `condition_errors` server-side; the server
 * remains the authority.
 */
export default function ConditionBuilder({
  condition, onChange, readOnly = false, depth = 0,
}: Props) {
  // ---- group ----
  if (isGroup(condition)) {
    const key: GroupKey = 'all' in condition ? 'all' : 'any';
    const clauses = (condition as any)[key] as Condition[];

    const setClauses = (next: Condition[]) => onChange({ [key]: next } as Condition);

    return (
      <div
        className={clsx(
          'rounded-md border border-gray-200 dark:border-gray-700 p-2 space-y-1.5',
          depth > 0 && 'bg-gray-50 dark:bg-gray-750'
        )}
      >
        <div className="flex items-center gap-1.5">
          <select
            value={key}
            disabled={readOnly}
            onChange={(e) =>
              onChange({ [e.target.value as GroupKey]: clauses } as Condition)
            }
            className={INPUT}
            aria-label="Match mode"
          >
            <option value="all">Match all of</option>
            <option value="any">Match any of</option>
          </select>
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            {clauses.length} condition{clauses.length === 1 ? '' : 's'}
          </span>
          <div className="ml-auto flex items-center gap-1">
            <button
              disabled={readOnly}
              onClick={() => setClauses([...clauses, blankLeaf()])}
              className="text-accent hover:underline text-[11px] disabled:opacity-40"
            >
              + condition
            </button>
            {depth < 3 && (
              <button
                disabled={readOnly}
                onClick={() => setClauses([...clauses, { all: [blankLeaf()] }])}
                className="text-accent hover:underline text-[11px] disabled:opacity-40"
              >
                + group
              </button>
            )}
            <button
              disabled={readOnly}
              onClick={() => onChange(clauses[0] ?? null)}
              className="text-gray-400 hover:text-red-500 disabled:opacity-40"
              title="Ungroup"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        {clauses.length === 0 && (
          <p className="text-[10px] text-gray-400">
            {key === 'any'
              ? 'An empty "any" never matches.'
              : 'An empty "all" always matches.'}
          </p>
        )}

        <div className="space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
          {clauses.map((clause, i) => (
            <div key={i} className="flex items-start gap-1">
              <div className="flex-1 min-w-0">
                <ConditionBuilder
                  condition={clause}
                  onChange={(next) =>
                    setClauses(clauses.map((c, j) => (j === i ? next : c)))
                  }
                  readOnly={readOnly}
                  depth={depth + 1}
                />
              </div>
              <button
                disabled={readOnly}
                onClick={() => setClauses(clauses.filter((_, j) => j !== i))}
                className="p-0.5 text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-40"
                title="Remove"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- not ----
  if (isNot(condition)) {
    return (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
            Not
          </span>
          <button
            disabled={readOnly}
            onClick={() => onChange(condition.not)}
            className="ml-auto text-gray-400 hover:text-red-500 disabled:opacity-40"
            title="Remove the negation"
          >
            <Trash2 size={12} />
          </button>
        </div>
        <ConditionBuilder
          condition={condition.not}
          onChange={(next) => onChange({ not: next })}
          readOnly={readOnly}
          depth={depth + 1}
        />
      </div>
    );
  }

  // ---- guard leaf ----
  if (isGuard(condition)) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0">Guard</span>
        <select
          value={condition.guard}
          disabled={readOnly}
          onChange={(e) => onChange({ guard: e.target.value })}
          className={`${INPUT} flex-1`}
        >
          {GUARD_TYPES.map((g) => (
            <option key={g.type} value={g.type}>{g.label}</option>
          ))}
        </select>
        <button
          disabled={readOnly}
          onClick={() => onChange(blankLeaf())}
          className="text-[10px] text-accent hover:underline disabled:opacity-40 shrink-0"
        >
          use a field
        </button>
      </div>
    );
  }

  // ---- field leaf ----
  if (isField(condition)) {
    const meta = CONDITION_OPERATORS.find((o) => o.op === condition.op);
    const unary = Boolean((meta as any)?.unary);
    const list = Boolean((meta as any)?.list);
    const known = COMMON_FIELDS.includes(condition.field as any);

    return (
      <div className="flex flex-wrap items-center gap-1">
        <select
          value={known ? condition.field : '__custom'}
          disabled={readOnly}
          onChange={(e) =>
            onChange({
              ...condition,
              field: e.target.value === '__custom' ? '' : e.target.value,
            })
          }
          className={`${INPUT} w-40`}
          aria-label="Field"
        >
          {COMMON_FIELDS.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
          <option value="__custom">other…</option>
        </select>

        {!known && (
          <input
            value={condition.field}
            disabled={readOnly}
            onChange={(e) => onChange({ ...condition, field: e.target.value })}
            placeholder="field path, e.g. customFields.x"
            className={`${INPUT} w-48 font-mono`}
            aria-label="Custom field path"
          />
        )}

        <select
          value={condition.op}
          disabled={readOnly}
          onChange={(e) => onChange({ ...condition, op: e.target.value })}
          className={`${INPUT} w-32`}
          aria-label="Operator"
        >
          {CONDITION_OPERATORS.map((o) => (
            <option key={o.op} value={o.op}>{o.label}</option>
          ))}
        </select>

        {!unary && (
          <input
            value={
              Array.isArray(condition.value)
                ? (condition.value as unknown[]).join(', ')
                : String(condition.value ?? '')
            }
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                ...condition,
                // `in`/`nin` need a list server-side, so split here rather than
                // letting the API reject it on save.
                value: list
                  ? e.target.value.split(',').map((v) => v.trim()).filter(Boolean)
                  : e.target.value,
              })
            }
            placeholder={list ? 'comma-separated' : 'value'}
            className={`${INPUT} w-36`}
            aria-label="Value"
          />
        )}

        {!condition.field.trim() && (
          <span className="text-[10px] text-red-500">field required</span>
        )}

        <button
          disabled={readOnly}
          onClick={() => onChange({ guard: GUARD_TYPES[0].type })}
          className="text-[10px] text-accent hover:underline disabled:opacity-40"
        >
          use a guard
        </button>
      </div>
    );
  }

  // ---- empty ----
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-500 dark:text-gray-400">
        No condition — this branch always matches.
      </span>
      {!readOnly && (
        <>
          <button
            onClick={() => onChange(blankLeaf())}
            className="flex items-center gap-1 text-[11px] text-accent hover:underline"
          >
            <Plus size={11} /> condition
          </button>
          <button
            onClick={() => onChange({ all: [blankLeaf()] })}
            className="flex items-center gap-1 text-[11px] text-accent hover:underline"
          >
            <Plus size={11} /> group
          </button>
        </>
      )}
    </div>
  );
}
